/**
 * twinflamePipeline — two opposing forces emanating from a shared
 * center, but never touching.
 *
 * A central point emits two flame-like structures that spiral
 * outward in opposite directions. Each flame is a thin, twisting
 * ribbon of light/gas. The flames are anti-correlated: where one
 * is bright, the other is dim, and they never cross — they're
 * separated by a thin "gap" of pure energy.
 *
 * Innovations:
 *   - Each flame is a parametric curve (a twisted ribbon) whose
 *     thickness and brightness vary along its length.
 *   - The two flames are phase-locked: flame B's brightness is the
 *     complement of flame A's. This creates a "yin-yang" effect.
 *   - The gap between them is highlighted with a sharp inverse-
 *     exponential brightness, suggesting the "pull" between them.
 *   - The flames' shapes are FBM-perturbed so they look like
 *     dancing fire, not just concentric circles.
 *   - A central "core" connects the two flames: a hot point where
 *     both forces originate.
 *
 * Audio:
 *   - bass: spiral twist speed
 *   - mid:  flame thickness
 *   - high: anti-correlation (how perfectly opposite they are)
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TwinflamePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}

// Position of flame A at parameter s in [-1, 1].
// s = -1 is at the center, s = +1 is far out.
vec2 flameA(float s, float t, float bass) {
  float r = abs(s) * 0.7;
  float angle = s * 6.0 + t * (0.5 + bass * 1.0);
  // FBM perturbation.
  float n = fbm(vec2(angle * 0.5, s * 3.0 + t * 0.3));
  float perturb = (n - 0.5) * 0.15;
  vec2 p = vec2(
    cos(angle) * r + perturb,
    sin(angle) * r * 0.8 + perturb * 0.5
  );
  // Asymmetric: the upper half is brighter.
  return p;
}

vec2 flameB(float s, float t, float bass) {
  float r = abs(s) * 0.7;
  float angle = -s * 6.0 - t * (0.5 + bass * 1.0) + 3.14159;
  float n = fbm(vec2(angle * 0.5 + 7.0, s * 3.0 - t * 0.3 + 5.0));
  float perturb = (n - 0.5) * 0.15;
  vec2 p = vec2(
    cos(angle) * r + perturb,
    sin(angle) * r * 0.8 + perturb * 0.5
  );
  return p;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — deep navy.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.02, 0.04, 0.10),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Render flame A — warm color (orange/red).
  vec3 colorA = vec3(0.95, 0.45, 0.25);
  float thicknessA = 0.020 + 0.015 * mid;
  float accumA = 0.0;
  for (int i = 0; i < 40; i++) {
    float s = (float(i) - 20.0) / 20.0;
    vec2 fp = flameA(s, uTime, bass);
    float d = length(p - fp);
    float intensity = exp(-d * d / (thicknessA * thicknessA));
    // Brightness falls off with distance from center.
    float brightness = 1.0 - abs(s) * 0.3;
    accumA += intensity * brightness;
  }

  // Render flame B — cool color (cyan/blue).
  vec3 colorB = vec3(0.40, 0.85, 0.95);
  float thicknessB = 0.020 + 0.015 * mid;
  float accumB = 0.0;
  for (int i = 0; i < 40; i++) {
    float s = (float(i) - 20.0) / 20.0;
    vec2 fp = flameB(s, uTime, bass);
    float d = length(p - fp);
    float intensity = exp(-d * d / (thicknessB * thicknessB));
    float brightness = 1.0 - abs(s) * 0.3;
    accumB += intensity * brightness;
  }

  // Anti-correlation: blend the two flames inversely.
  float anti = 0.7 + 0.3 * high;
  float intensityA = accumA * (1.0 - 0.5 * accumB * anti);
  float intensityB = accumB * (1.0 - 0.5 * accumA * anti);

  col += colorA * intensityA * 0.7;
  col += colorB * intensityB * 0.7;

  // The "gap" between the flames — a thin bright line where the two
  // forces meet but never touch.
  // The gap is roughly along the y-axis (where the two flames' centers
  // separate). It's brightest where both flames are simultaneously near.
  float gapIntensity = accumA * accumB * 4.0;
  vec3 gapColor = vec3(1.0, 0.95, 0.8);
  col += gapColor * gapIntensity * 0.8;

  // Central core — a hot point where both flames originate.
  float coreGlow = exp(-r * r * 80.0);
  col += vec3(1.0, 0.95, 0.7) * coreGlow * 1.2;

  // Outer halo — the merged energy of both flames.
  float halo = exp(-r * 4.0) * 0.3;
  col += vec3(0.9, 0.7, 0.5) * halo;

  // Sparks at the tips of each flame.
  for (int i = 0; i < 30; i++) {
    float fi = float(i);
    vec2 fpA = flameA(0.95, uTime + fi * 0.1, bass);
    vec2 fpB = flameB(-0.95, uTime + fi * 0.1, bass);
    float dA = length(p - fpA);
    float dB = length(p - fpB);
    col += vec3(1.0, 0.7, 0.4) * exp(-dA * 200.0) * 0.5;
    col += vec3(0.5, 0.9, 1.0) * exp(-dB * 200.0) * 0.5;
  }

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeTwinflameBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTwinflameRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTwinflamePipeline(gl: WebGL2RenderingContext): TwinflamePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTwinflame(
  gl: WebGL2RenderingContext,
  pipe: TwinflamePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
